import { useMemo } from "react";
import ReactECharts from "echarts-for-react";

import type { WidgetComponentProps } from "../types";
import { useSummaryQuery } from "../useSummaryQuery";

function buildBuckets(flightDates: string[], days: number) {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const dayMs = 24 * 3600 * 1000;
  const labels: string[] = [];
  const counts: number[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const dayStart = new Date(now.getTime() - i * dayMs);
    const dayEnd = new Date(dayStart.getTime() + dayMs);
    let count = 0;
    for (const dateStr of flightDates) {
      const d = new Date(dateStr);
      d.setHours(0, 0, 0, 0);
      if (d >= dayStart && d < dayEnd) count += 1;
    }
    labels.push(
      dayStart.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      }),
    );
    counts.push(count);
  }
  const avg = counts.map((_, i) => {
    const start = Math.max(0, i - 2);
    const slice = counts.slice(start, i + 1);
    return slice.length ? Number((slice.reduce((a, b) => a + b, 0) / slice.length).toFixed(2)) : 0;
  });
  return { labels, counts, avg };
}

export function ActivityChartWidget({ sizeVariant }: WidgetComponentProps) {
  const { data, isLoading } = useSummaryQuery();

  const days = sizeVariant === "s" ? 7 : sizeVariant === "m" ? 14 : 30;
  const showTrend = sizeVariant !== "s";
  const showAxis = sizeVariant !== "s";

  const option = useMemo(() => {
    const { labels, counts, avg } = buildBuckets(data?.flight_dates ?? [], days);
    const series: Record<string, unknown>[] = [
      {
        name: "Flights",
        type: "bar",
        data: counts,
        barMaxWidth: sizeVariant === "l" ? 18 : 26,
        itemStyle: { color: "#FF6B1A", borderRadius: [3, 3, 0, 0] },
        emphasis: { itemStyle: { color: "#FF8534" } },
      },
    ];
    if (showTrend) {
      series.push({
        name: "Trend",
        type: "line",
        data: avg,
        smooth: 0.3,
        showSymbol: false,
        lineStyle: { width: 2, color: "#FBBF24" },
        areaStyle: { color: "rgba(251, 191, 36, 0.1)" },
      });
    }

    return {
      grid: showAxis
        ? { top: 20, right: 12, bottom: 24, left: 30 }
        : { top: 4, right: 4, bottom: 4, left: 4 },
      xAxis: {
        type: "category",
        data: labels,
        show: showAxis,
        axisLabel: {
          color: "#A0AAB8",
          fontSize: 10,
          interval: sizeVariant === "l" ? 3 : 0,
        },
        axisLine: { lineStyle: { color: "#2A3444" } },
        axisTick: { show: false },
      },
      yAxis: {
        type: "value",
        minInterval: 1,
        show: showAxis,
        axisLabel: { color: "#A0AAB8", fontSize: 10 },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: "#2A3444", type: "dashed" } },
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: "rgba(10, 14, 20, 0.96)",
        borderColor: "#2A3444",
        textStyle: { color: "#E8ECF1", fontSize: 12 },
      },
      series,
    };
  }, [data?.flight_dates, days, showAxis, showTrend, sizeVariant]);

  if (isLoading) return <p className="widget-empty">Loading…</p>;

  return (
    <div className={`activity-chart activity-chart--${sizeVariant}`}>
      {sizeVariant !== "s" && (
        <div className="activity-chart__head">
          <p className="eyebrow">Flight activity · last {days} days</p>
        </div>
      )}
      <div className="activity-chart__body">
        <ReactECharts
          option={option}
          notMerge
          style={{ height: "100%", width: "100%" }}
        />
      </div>
    </div>
  );
}
